import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'

import { listSystemConfig, updateSystemConfigValue } from '@/api/system_config'
import {
  AUCTION_CALL_KEYS,
  AUCTION_CALL_PLACEHOLDERS,
  AUCTION_CALL_TEMPLATE_DEFAULT,
  AUCTION_CALL_WINDOW_DEFAULT,
  AUCTION_SMS_KEYS,
  AUCTION_SMS_PLACEHOLDERS,
  AUCTION_SMS_TEMPLATE_DEFAULT,
  deleteAuctionSmsRecipient,
  guessCallName,
  isValidCallWindow,
  listAuctionSmsRecipients,
  renderAuctionCall,
  renderAuctionSms,
  upsertAuctionSmsRecipient,
  type AuctionRecipientChannel,
  type AuctionSmsRecipient,
} from '@/api/auction-sms'
import { countSmsSegments } from '@/utils/sms'
import { digitsOnly, formatPhoneDisplay } from '@/utils/phone'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PillToggle } from '@/components/ui/PillToggle'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'

/**
 * /sber-sms-settings: «Сбер → SMS и звонки». SMS и звонки дилерам о старте
 * торгов по лоту. Выключатели, шаблоны и окно звонков в system_config,
 * получатели и их канал в auction_sms_recipients (миграции stryzh
 * 20261103160000, 20261105220000). SMS шлёт триггер БД на переходе лида в
 * «Торги»; звонки тот же переход ставит в очередь call_outbox, звонит робот.
 * Пока выключено, в журнал пишутся строки skipped.
 */

const SAMPLE = { car: 'BMW 5 серии 2013', end: '16:40', link: 'https://carlink.ru/l/a57e56' }
const CALL_SAMPLE = { name: 'Арсений', car: 'Тойота Камри', year: 'две тысячи девятнадцатого года' }
const CFG_QK = ['system-config', 'sber-sms'] as const
const RCP_QK = ['auction-sms-recipients'] as const

export function SberSmsSettingsPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()

  const cfgQ = useQuery({ queryKey: CFG_QK, queryFn: listSystemConfig, staleTime: 30_000 })
  const rcpQ = useQuery({ queryKey: RCP_QK, queryFn: listAuctionSmsRecipients })

  const cfg = useMemo(() => {
    const byKey = new Map((cfgQ.data ?? []).map((e) => [e.key, e.value]))
    return {
      enabled: (byKey.get(AUCTION_SMS_KEYS.enabled) ?? 'false').trim() === 'true',
      template: (byKey.get(AUCTION_SMS_KEYS.template) ?? '').trim() || AUCTION_SMS_TEMPLATE_DEFAULT,
      callEnabled: (byKey.get(AUCTION_CALL_KEYS.enabled) ?? 'false').trim() === 'true',
      callTemplate: (byKey.get(AUCTION_CALL_KEYS.template) ?? '').trim() || AUCTION_CALL_TEMPLATE_DEFAULT,
      windowFrom: (byKey.get(AUCTION_CALL_KEYS.windowFrom) ?? '').trim() || AUCTION_CALL_WINDOW_DEFAULT.from,
      windowTo: (byKey.get(AUCTION_CALL_KEYS.windowTo) ?? '').trim() || AUCTION_CALL_WINDOW_DEFAULT.to,
    }
  }, [cfgQ.data])

  const [templateDraft, setTemplateDraft] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const template = templateDraft ?? cfg.template
  const templateDirty = templateDraft != null && templateDraft.trim() !== cfg.template
  const templateInvalid = template.trim() === '' || !template.includes('{link}')

  const cfgMut = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => updateSystemConfigValue(key, value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CFG_QK })
      setTemplateDraft(null)
      setErrorMsg(null)
    },
    onError: (e: Error) => setErrorMsg(e.message),
  })

  const preview = renderAuctionSms(template, SAMPLE)
  const segments = countSmsSegments(preview)

  if (cfgQ.isLoading || rcpQ.isLoading) return <Loader />
  const loadError = (cfgQ.error ?? rcpQ.error) as Error | null

  return (
    <div>
      <PageHeader title={t('settings.sberSms.title')} subtitle={t('settings.sberSms.subtitle')} />
      {loadError ? <Banner text={t('settings.error', { message: loadError.message })} /> : null}

      <div className="flex flex-col gap-6">
        <section className={cardCls}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-base font-semibold text-[color:var(--stryzh-color-text-primary)]">
                {t('settings.sberSms.enabled.label')}
              </div>
              <div className="mt-1 text-xs text-[color:var(--stryzh-color-text-secondary)]">
                {t(cfg.enabled ? 'settings.sberSms.enabled.on' : 'settings.sberSms.enabled.off')}
              </div>
            </div>
            <PillToggle
              checked={cfg.enabled}
              label={t(cfg.enabled ? 'settings.sberSms.enabled.toggleOn' : 'settings.sberSms.enabled.toggleOff')}
              onChange={(v) => cfgMut.mutate({ key: AUCTION_SMS_KEYS.enabled, value: v ? 'true' : 'false' })}
            />
          </div>
        </section>

        <section className={cardCls}>
          <div className="text-base font-semibold text-[color:var(--stryzh-color-text-primary)]">
            {t('settings.sberSms.template.label')}
          </div>
          <div className="mt-1 text-xs text-[color:var(--stryzh-color-text-secondary)]">
            {t('settings.sberSms.template.hint', { placeholders: AUCTION_SMS_PLACEHOLDERS.join(' ') })}
          </div>
          <textarea
            value={template}
            onChange={(e) => setTemplateDraft(e.target.value)}
            rows={2}
            className="mt-3 w-full resize-y rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)] px-3 py-2 font-mono text-sm text-[color:var(--stryzh-color-text-primary)]"
          />
          <div className="mt-2 text-[11px] uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
            {t('settings.sberSms.template.preview')}
          </div>
          <div className="mt-1 rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface-elevated)] p-2 text-sm">
            <code className="break-all font-mono">{preview}</code>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-[color:var(--stryzh-color-text-secondary)]">
            <span>
              {t(segments === 1 ? 'settings.sms.charCountOne' : 'settings.sms.charCountMany', { chars: preview.length, segments })}
            </span>
            {templateInvalid ? <span className="text-red-300">{t('settings.sms.linkRequired')}</span> : null}
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant="primary"
              disabled={!templateDirty || templateInvalid || cfgMut.isPending}
              onClick={() => cfgMut.mutate({ key: AUCTION_SMS_KEYS.template, value: template.trim() })}
            >
              {cfgMut.isPending ? t('settings.saving') : t('common.save')}
            </Button>
            <Button type="button" variant="secondary" disabled={!templateDirty} onClick={() => setTemplateDraft(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </section>

        <CallsSection
          enabled={cfg.callEnabled}
          template={cfg.callTemplate}
          windowFrom={cfg.windowFrom}
          windowTo={cfg.windowTo}
          onError={setErrorMsg}
        />

        <RecipientsSection items={rcpQ.data ?? []} onError={setErrorMsg} />

        {errorMsg ? <Banner text={errorMsg} /> : null}
      </div>
    </div>
  )
}

const cardCls =
  'rounded-lg border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] p-4'

function CallsSection({
  enabled,
  template: savedTemplate,
  windowFrom,
  windowTo,
  onError,
}: {
  enabled: boolean
  template: string
  windowFrom: string
  windowTo: string
  onError: (msg: string | null) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [templateDraft, setTemplateDraft] = useState<string | null>(null)
  const [windowDraft, setWindowDraft] = useState<{ from: string; to: string } | null>(null)

  const template = templateDraft ?? savedTemplate
  const templateDirty = templateDraft != null && templateDraft.trim() !== savedTemplate
  const templateInvalid = template.trim() === ''
  const win = windowDraft ?? { from: windowFrom, to: windowTo }
  const windowDirty = windowDraft != null && (windowDraft.from !== windowFrom || windowDraft.to !== windowTo)
  const windowInvalid = !isValidCallWindow(win.from, win.to)

  const saveMut = useMutation({
    mutationFn: async (entries: { key: string; value: string }[]) => {
      for (const e of entries) await updateSystemConfigValue(e.key, e.value)
    },
    onSuccess: (_, entries) => {
      void qc.invalidateQueries({ queryKey: CFG_QK })
      if (entries.some((e) => e.key === AUCTION_CALL_KEYS.template)) setTemplateDraft(null)
      if (entries.some((e) => e.key === AUCTION_CALL_KEYS.windowFrom)) setWindowDraft(null)
      onError(null)
    },
    onError: (e: Error) => onError(e.message),
  })

  const preview = renderAuctionCall(template, CALL_SAMPLE)

  return (
    <>
      <section className={cardCls}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-[color:var(--stryzh-color-text-primary)]">
              {t('settings.sberSms.calls.enabled.label')}
            </div>
            <div className="mt-1 text-xs text-[color:var(--stryzh-color-text-secondary)]">
              {t(enabled ? 'settings.sberSms.calls.enabled.on' : 'settings.sberSms.calls.enabled.off')}
            </div>
          </div>
          <PillToggle
            checked={enabled}
            label={t(enabled ? 'settings.sberSms.calls.enabled.toggleOn' : 'settings.sberSms.calls.enabled.toggleOff')}
            onChange={(v) => saveMut.mutate([{ key: AUCTION_CALL_KEYS.enabled, value: v ? 'true' : 'false' }])}
          />
        </div>
      </section>

      <section className={cardCls}>
        <div className="text-base font-semibold text-[color:var(--stryzh-color-text-primary)]">
          {t('settings.sberSms.calls.template.label')}
        </div>
        <div className="mt-1 text-xs text-[color:var(--stryzh-color-text-secondary)]">
          {t('settings.sberSms.calls.template.hint', { placeholders: AUCTION_CALL_PLACEHOLDERS.join(' ') })}
        </div>
        <textarea
          value={template}
          onChange={(e) => setTemplateDraft(e.target.value)}
          rows={2}
          className="mt-3 w-full resize-y rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)] px-3 py-2 font-mono text-sm text-[color:var(--stryzh-color-text-primary)]"
        />
        <div className="mt-2 text-[11px] uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
          {t('settings.sberSms.template.preview')}
        </div>
        <div className="mt-1 rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface-elevated)] p-2 text-sm">
          {preview}
        </div>
        {templateInvalid ? (
          <div className="mt-1 text-xs text-red-300">{t('settings.sberSms.calls.template.required')}</div>
        ) : null}
        <div className="mt-3 flex gap-3">
          <Button
            type="button"
            variant="primary"
            disabled={!templateDirty || templateInvalid || saveMut.isPending}
            onClick={() => saveMut.mutate([{ key: AUCTION_CALL_KEYS.template, value: template.trim() }])}
          >
            {saveMut.isPending ? t('settings.saving') : t('common.save')}
          </Button>
          <Button type="button" variant="secondary" disabled={!templateDirty} onClick={() => setTemplateDraft(null)}>
            {t('common.cancel')}
          </Button>
        </div>

        <div className="mt-6 text-base font-semibold text-[color:var(--stryzh-color-text-primary)]">
          {t('settings.sberSms.calls.window.label')}
        </div>
        <div className="mt-1 text-xs text-[color:var(--stryzh-color-text-secondary)]">
          {t('settings.sberSms.calls.window.hint')}
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="w-32">
            <Input
              type="time"
              label={t('settings.sberSms.calls.window.from')}
              value={win.from}
              onChange={(e) => setWindowDraft({ ...win, from: e.target.value })}
            />
          </div>
          <div className="w-32">
            <Input
              type="time"
              label={t('settings.sberSms.calls.window.to')}
              value={win.to}
              onChange={(e) => setWindowDraft({ ...win, to: e.target.value })}
            />
          </div>
          <Button
            type="button"
            variant="primary"
            disabled={!windowDirty || windowInvalid || saveMut.isPending}
            onClick={() =>
              saveMut.mutate([
                { key: AUCTION_CALL_KEYS.windowFrom, value: win.from },
                { key: AUCTION_CALL_KEYS.windowTo, value: win.to },
              ])
            }
          >
            {saveMut.isPending ? t('settings.saving') : t('common.save')}
          </Button>
          <Button type="button" variant="secondary" disabled={!windowDirty} onClick={() => setWindowDraft(null)}>
            {t('common.cancel')}
          </Button>
        </div>
        {windowInvalid ? (
          <div className="mt-1 text-xs text-red-300">{t('settings.sberSms.calls.window.invalid')}</div>
        ) : null}
      </section>
    </>
  )
}

const EMPTY_FORM = { dealer: '', full_name: '', phone: '' }

const CHANNELS: AuctionRecipientChannel[] = ['sms', 'sms_call']

/** Имя в звонке: сохраняется, когда поле теряет фокус. */
function CallNameCell({ value, onSave }: { value: string | null; onSave: (v: string | null) => void }) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<string | null>(null)
  const shown = draft ?? value ?? ''
  return (
    <input
      value={shown}
      placeholder={t('settings.sberSms.recipients.callNamePh')}
      aria-label={t('settings.sberSms.recipients.col.callName')}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft == null) return
        const next = draft.trim() || null
        setDraft(null)
        if (next !== value) onSave(next)
      }}
      className="w-32 rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)] px-2 py-1 text-sm text-[color:var(--stryzh-color-text-primary)]"
    />
  )
}

function RecipientsSection({
  items,
  onError,
}: {
  items: AuctionSmsRecipient[]
  onError: (msg: string | null) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [form, setForm] = useState(EMPTY_FORM)
  const [toDelete, setToDelete] = useState<AuctionSmsRecipient | null>(null)

  const refresh = () => void qc.invalidateQueries({ queryKey: RCP_QK })
  const saveMut = useMutation({
    mutationFn: upsertAuctionSmsRecipient,
    onSuccess: () => {
      refresh()
      setForm(EMPTY_FORM)
      onError(null)
    },
    onError: (e: Error) => onError(e.message),
  })
  const delMut = useMutation({
    mutationFn: deleteAuctionSmsRecipient,
    onSuccess: () => {
      refresh()
      setToDelete(null)
      onError(null)
    },
    onError: (e: Error) => onError(e.message),
  })

  const phoneDigits = normalizeRecipientPhone(form.phone)
  const canAdd = form.dealer.trim() !== '' && form.full_name.trim() !== '' && phoneDigits != null

  return (
    <section className={cardCls}>
      <div className="text-base font-semibold text-[color:var(--stryzh-color-text-primary)]">
        {t('settings.sberSms.recipients.label')}
      </div>
      <div className="mt-1 text-xs text-[color:var(--stryzh-color-text-secondary)]">
        {t('settings.sberSms.recipients.hint')}
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
            <tr>
              <th className="py-2 pr-3">{t('settings.sberSms.recipients.col.dealer')}</th>
              <th className="py-2 pr-3">{t('settings.sberSms.recipients.col.name')}</th>
              <th className="py-2 pr-3">{t('settings.sberSms.recipients.col.phone')}</th>
              <th className="py-2 pr-3">{t('settings.sberSms.recipients.col.channel')}</th>
              <th className="py-2 pr-3">{t('settings.sberSms.recipients.col.callName')}</th>
              <th className="py-2 pr-3">{t('settings.sberSms.recipients.col.active')}</th>
              <th className="py-2 pr-3">{t('settings.sberSms.recipients.col.test')}</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-[color:var(--stryzh-color-border-subtle)]">
                <td className="py-2 pr-3">{r.dealer}</td>
                <td className="py-2 pr-3">{r.full_name}</td>
                <td className="py-2 pr-3 tabular-nums">{formatPhoneDisplay('+' + r.phone)}</td>
                <td className="py-2 pr-3">
                  <select
                    value={r.channel}
                    aria-label={t('settings.sberSms.recipients.col.channel')}
                    onChange={(e) => saveMut.mutate({ ...r, channel: e.target.value as AuctionRecipientChannel })}
                    className="rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)] px-2 py-1 text-sm text-[color:var(--stryzh-color-text-primary)]"
                  >
                    {CHANNELS.map((c) => (
                      <option key={c} value={c}>
                        {t(`settings.sberSms.recipients.channel.${c}`)}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="py-2 pr-3">
                  <CallNameCell value={r.call_name} onSave={(v) => saveMut.mutate({ ...r, call_name: v })} />
                </td>
                <td className="py-2 pr-3">
                  <PillToggle
                    checked={r.is_active}
                    label={t(r.is_active ? 'settings.sberSms.recipients.active' : 'settings.sberSms.recipients.inactive')}
                    onChange={(v) => saveMut.mutate({ ...r, is_active: v })}
                  />
                </td>
                <td className="py-2 pr-3">
                  <PillToggle
                    checked={r.is_test}
                    label={t(r.is_test ? 'settings.sberSms.recipients.test' : 'settings.sberSms.recipients.notTest')}
                    onChange={(v) => saveMut.mutate({ ...r, is_test: v })}
                  />
                </td>
                <td className="py-2 text-right">
                  <button
                    type="button"
                    aria-label={t('settings.sberSms.recipients.delete')}
                    title={t('settings.sberSms.recipients.delete')}
                    onClick={() => setToDelete(r)}
                    className="text-[color:var(--stryzh-color-text-secondary)] hover:text-red-300"
                  >
                    <Trash2 size={16} aria-hidden />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-3 text-[color:var(--stryzh-color-text-secondary)]">
                  {t('settings.sberSms.recipients.empty')}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <form
        className="mt-4 grid gap-3 md:grid-cols-[1fr_1.5fr_1fr_auto] md:items-end"
        onSubmit={(e) => {
          e.preventDefault()
          if (!canAdd || phoneDigits == null) return
          const fullName = form.full_name.trim()
          saveMut.mutate({
            dealer: form.dealer.trim(),
            full_name: fullName,
            phone: phoneDigits,
            is_active: true,
            call_name: guessCallName(fullName),
          })
        }}
      >
        <Input
          label={t('settings.sberSms.recipients.col.dealer')}
          value={form.dealer}
          onChange={(e) => setForm({ ...form, dealer: e.target.value })}
        />
        <Input
          label={t('settings.sberSms.recipients.col.name')}
          value={form.full_name}
          onChange={(e) => setForm({ ...form, full_name: e.target.value })}
        />
        <Input
          label={t('settings.sberSms.recipients.col.phone')}
          value={form.phone}
          placeholder="+7 926 111 59 83"
          error={form.phone.trim() !== '' && phoneDigits == null ? t('settings.sberSms.recipients.badPhone') : undefined}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <Button type="submit" variant="primary" disabled={!canAdd || saveMut.isPending}>
          {t('settings.sberSms.recipients.add')}
        </Button>
      </form>

      <ConfirmDialog
        open={toDelete != null}
        title={t('settings.sberSms.recipients.deleteTitle')}
        confirmLabel={t('settings.sberSms.recipients.delete')}
        confirmVariant="danger"
        onConfirm={() => toDelete && delMut.mutate(toDelete.id)}
        onCancel={() => setToDelete(null)}
      >
        {t('settings.sberSms.recipients.deleteText', { name: toDelete?.full_name ?? '' })}
      </ConfirmDialog>
    </section>
  )
}

/** «+7 926 111 59 83» / «8 926…» → «79261115983»; иначе null. */
function normalizeRecipientPhone(raw: string): string | null {
  let d = digitsOnly(raw)
  if (/^8\d{10}$/.test(d)) d = '7' + d.slice(1)
  if (/^9\d{9}$/.test(d)) d = '7' + d
  return /^7\d{10}$/.test(d) ? d : null
}

function Banner({ text }: { text: string }) {
  return <div className="rounded border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">{text}</div>
}
